/**
 * Certification Unlock Policy
 *
 * This module encodes the business rules governing when a manager can unlock a certification
 * scenario for their direct report. It implements a strict policy that prevents invalid unlock
 * requests before they reach the backend API.
 *
 * The policy enforces six validation rules in fail-fast order:
 * 1. All required context must be present (manager, user, scenario, certification IDs)
 * 2. Target user must be a direct report of the requesting manager
 * 3. Scenario ID must match the active certification ID (scope validation)
 * 4. Target user must not have already passed the certification
 * 5. Certification must currently be in a locked state
 *
 * All checks are deterministic, synchronous, and have no side effects—this is pure business logic.
 */

// ================================ Input Types ================================

/**
 * Context required to evaluate an unlock request.
 *
 * @property activeCertificationId - The certification currently being managed (null if no active cert)
 * @property directReportUserIds - List of user IDs who report to the manager (immutable reference)
 * @property managerUserId - The user requesting the unlock
 * @property scenarioId - The scenario ID the manager is trying to unlock (null if not selected)
 * @property targetUserId - The direct report whose certification lock is being evaluated for removal
 * @property targetUserMostRecentCertPassed - Whether the target has passed their most recent attempt (null if unknown)
 * @property targetUserScenarioLocked - Whether the target's certification is currently locked (null if unknown)
 */
export type CertificationUnlockInput = {
  activeCertificationId: number | null;
  directReportUserIds: string[];
  managerUserId: string;
  scenarioId: number | null;
  targetUserId: string;
  targetUserMostRecentCertPassed: boolean | null;
  targetUserScenarioLocked: boolean | null;
};

/**
 * Reason codes representing all possible outcomes of an unlock evaluation.
 *
 * Success outcome:
 * - 'ok' — All policy checks passed; unlock is allowed.
 *
 * Policy denial reasons:
 * - 'missing_context' — Required context (IDs or state) is missing or null.
 * - 'not_direct_report' — Target user is not in the manager's direct reports list.
 * - 'scenario_mismatch' — Scenario ID does not match the active certification context.
 * - 'already_certified' — Target user has already passed; no need to unlock.
 * - 'not_locked' — Certification is not locked; nothing to unlock.
 *
 * Used by callers for granular error messaging and diagnostic logging.
 */
export type CertificationUnlockReason =
  | 'ok'
  | 'missing_context'
  | 'not_direct_report'
  | 'scenario_mismatch'
  | 'already_certified'
  | 'not_locked';

/**
 * Result of evaluating a certification unlock request against the policy.
 *
 * @property allowed - Boolean flag indicating if the unlock request is permitted
 * @property reason - Detailed code explaining the decision (used for error messaging)
 */
export type CertificationUnlockDecision = {
  allowed: boolean;
  reason: CertificationUnlockReason;
};


// ================================ Policy Evaluation ================================

/**
 * Evaluates whether a manager can unlock a certification scenario for a direct report.
 *
 * The policy implements a strict permission model with six validation steps, evaluated
 * in order of importance. Each step returns immediately on failure (fail-fast pattern),
 * ensuring maximum efficiency and clear diagnostic information.
 *
 * Validation sequence:
 * 1. Context validation — Ensures all IDs and state flags are present
 * 2. Authority check — Verifies the target is a direct report
 * 3. Scope validation — Prevents unlocking a different certification than intended
 * 4. Status check — Prevents double-unlocking already-certified users
 * 5. Lock state check — Ensures there is actually a lock to remove
 *
 * @param input - The unlock request context
 * @returns A decision object with allowed (boolean) and reason (code) properties
 *
 * @example
 * const policy = evaluateCertificationUnlock({
 *   managerUserId: 'mgr-123',
 *   targetUserId: 'user-456',
 *   scenarioId: 100,
 *   activeCertificationId: 100,
 *   directReportUserIds: ['user-456', 'user-789'],
 *   targetUserMostRecentCertPassed: false,
 *   targetUserScenarioLocked: true,
 * });
 *
 * if (policy.allowed) {
 *   // Proceed with unlock API call
 * } else {
 *   // Display denial message based on policy.reason
 * }
 */
export const evaluateCertificationUnlock = (
  input: CertificationUnlockInput,
): CertificationUnlockDecision => {
  // ──────────────────────────────────────────────────────────────────────────────
  // Rule 1: Context Validation
  // ──────────────────────────────────────────────────────────────────────────────
  // Ensure all required identifiers are present. Missing context indicates either
  // a caller error or incomplete data hydration. Extract to intermediate variables
  // for readability in conditional logic.
  const noManagerId = !input.managerUserId;
  const noTargetUserId = !input.targetUserId;
  const noScenarioId = !input.scenarioId;
  const noActiveCertificationId = !input.activeCertificationId;

  if (noManagerId || noTargetUserId || noScenarioId || noActiveCertificationId) {
    return { allowed: false, reason: 'missing_context' };
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Rule 2: Authority Check (Direct Report Validation)
  // ──────────────────────────────────────────────────────────────────────────────
  // Verify the target user is in the manager's direct report list. This is the
  // primary authorization check—managers can only unlock certifications for their
  // own teams. The directReportUserIds list is immutable for this operation.
  if (!input.directReportUserIds.includes(input.targetUserId)) {
    return { allowed: false, reason: 'not_direct_report' };
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Rule 3: Scope Validation (Certification Context Matching)
  // ──────────────────────────────────────────────────────────────────────────────
  // Verify the scenario ID matches the currently active certification. This prevents
  // a race condition where a manager selects a user/certification combination,
  // the active certification changes, and the unlock is applied to the wrong context.
  // Example: Manager has cert 100 active, clicks unlock. Before submit, cert 200 becomes
  // active. Without this check, cert 100 might be unlocked instead of cert 200.
  if (input.scenarioId !== input.activeCertificationId) {
    return { allowed: false, reason: 'scenario_mismatch' };
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Rule 4: Certification Status Check
  // ──────────────────────────────────────────────────────────────────────────────
  // Reject unlock if the user has already passed the certification. Once certified,
  // there is no lock to remove. This prevents unnecessary API calls and confusion—
  // the UI should not present an unlock option for a passed certification.
  if (input.targetUserMostRecentCertPassed === true) {
    return { allowed: false, reason: 'already_certified' };
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // Rule 5: Lock State Validation
  // ──────────────────────────────────────────────────────────────────────────────
  // Verify the certification is actually locked. If it's not locked (e.g., user is
  // on their first attempt and has not triggered the lock), there's nothing to unlock.
  // This also prevents duplicate unlock operations.
  if (input.targetUserScenarioLocked !== true) {
    return { allowed: false, reason: 'not_locked' };
  }

  // ──────────────────────────────────────────────────────────────────────────────
  // All Rules Passed
  // ──────────────────────────────────────────────────────────────────────────────
  // The unlock request is permitted. Return success to the caller, who will typically
  // proceed with an API call to persist the unlock state.
  return { allowed: true, reason: 'ok' };
};
